import { DurationChoices } from '../../components/PlayingPreferences';
import { DURATION_OPTIONS, type PlayTogetherDuration } from '@league/shared/schemas';
import { comparePeople } from './people-display';
import type { PlayTogetherGroup, PlayTogetherGroupsResponse, PlayTogetherGroupResponse, SitWithRequest } from '@league/shared/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Button, ErrorNote, Input } from '../../components/ui';
import { ApiError, apiFetch, apiPost, apiPatch } from '../../lib/api';
import { PlayTogetherActionsMenu } from './PlayTogetherActionsMenu';
import { PlayTogetherRequests } from './PlayTogetherRequests';

interface Person { enrollmentId: string; name: string; firstName: string; lastName: string; email: string | null; enrolled: boolean }

export function SeatingGroups({ leagueId, people, requests = [] }: { leagueId: string; people: Person[]; requests?: SitWithRequest[] }) {
  const client = useQueryClient();
  const path = `/api/director/leagues/${leagueId}/play-together-groups`;
  const key = ['play-together-groups', leagueId];
  const query = useQuery({ queryKey: key, queryFn: () => apiFetch<PlayTogetherGroupsResponse>(path) });
  const [editing, setEditing] = useState<PlayTogetherGroup | 'new' | null>(null);
  const [duration,setDuration]=useState<PlayTogetherDuration>('first_session');
  const [chosen, setChosen] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [removing, setRemoving] = useState<PlayTogetherGroup | null>(null);
  const confirm = useRef<HTMLDialogElement>(null);
  const editor = useRef<HTMLDivElement>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const peopleById = new Map(people.map((p) => [p.enrollmentId, p]));
  const groupNames = (group: PlayTogetherGroup) => [...group.enrollmentIds].sort((a, b) => {
    const first = peopleById.get(a); const second = peopleById.get(b);
    return first && second ? comparePeople(first, second) : first ? -1 : second ? 1 : a.localeCompare(b);
  }).map((id) => peopleById.get(id)?.name ?? 'Former roster player').join(', ');
  const needle = search.trim().toLowerCase();
  const choices = people.filter((p) => p.enrolled && (!needle ||
    `${p.firstName} ${p.lastName} ${p.email ?? ''}`.toLowerCase().includes(needle))).sort(comparePeople);
  const formerSelected = people.filter((p) => !p.enrolled && chosen.includes(p.enrollmentId)).sort(comparePeople);
  const refresh = () => client.invalidateQueries({ queryKey: key });
  const save = useMutation({
    mutationFn: () => editing === 'new'
      ? apiPost<PlayTogetherGroupResponse>(path, { enrollmentIds: chosen, duration })
      : apiPatch<PlayTogetherGroupResponse>(`${path}/${(editing as PlayTogetherGroup).id}`, {
          enrollmentIds: chosen, duration, revision: (editing as PlayTogetherGroup).revision,
        }),
    onSuccess: async () => { setEditing(null); await refresh(); addButton.current?.focus(); },
  });
  const remove = useMutation({
    mutationFn: (group: PlayTogetherGroup) => apiFetch(`${path}/${group.id}`, {
      method: 'DELETE', body: JSON.stringify({ revision: group.revision }),
    }),
    onSuccess: async () => {
      confirm.current?.close(); setRemoving(null); await refresh(); addButton.current?.focus();
    },
  });
  const begin = (group: PlayTogetherGroup | 'new') => {
    setDuration(group === 'new' ? 'first_session' : group.duration); save.reset(); setSearch(''); setEditing(group); setChosen(group === 'new' ? [] : group.enrollmentIds);
    requestAnimationFrame(() => editor.current?.focus());
  };
  const invalid = chosen.some((id) => !people.some((p) => p.enrollmentId === id && p.enrolled));
  return (
    <section className="flex flex-col gap-3" aria-label="Play Together">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-ui text-[13px] font-semibold uppercase tracking-[0.1em] text-ink-2">Play Together</h3>
          <p className="mt-1 text-[12px] text-ink-3">Seat these players together when possible.</p>
        </div>
        <Button ref={addButton} variant="secondary" disabled={people.filter((p) => p.enrolled).length < 2 || editing !== null}
          onClick={() => begin('new')}>Add Group</Button>
      </div>
      {query.isPending ? <p className="text-sm text-ink-3">Loading groups…</p> : null}
      {query.isError ? <ErrorNote>Could not load groups. Please refresh the roster.</ErrorNote> : null}
      {(query.data?.groups.length ?? 0) > 0 ? <ul className="flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
        {query.data?.groups.map((group) => <li key={group.id} className="flex items-center justify-between gap-4 px-5 py-3">
          <span className="text-[14px]">{groupNames(group)}<span className="block text-[12px] text-ink-3">{DURATION_OPTIONS.find(o=>o.value===group.duration)?.label ?? 'Every session'}</span></span>
          <PlayTogetherActionsMenu label={groupNames(group)} onEdit={() => begin(group)} onRemove={() => {
            remove.reset(); setRemoving(group); confirm.current?.showModal();
          }} />
        </li>)}
      </ul> : null}
      <PlayTogetherRequests leagueId={leagueId} requests={requests} people={people} />
      {editing ? <div ref={editor} tabIndex={-1} className="flex flex-col gap-3 rounded-md border border-light bg-sand p-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-deep">
        <h4 className="font-medium">{editing === 'new' ? 'Add Group' : 'Edit Group'}</h4>
        <DurationChoices value={duration} onChange={setDuration} disabled={save.isPending}/>
        <p className="text-[13px] text-ink-2">Choose at least two players. {chosen.length} selected.</p>
        <label className="flex flex-col gap-1 text-[13px] text-ink-2">
          Search players
          <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search players" />
        </label>
        {choices.length === 0 ? <p className="text-[13px] text-ink-2">No players match your search.</p> : null}
        <div className="grid max-h-80 grid-cols-1 gap-2 overflow-y-auto p-1 sm:grid-cols-2">
          {choices.map((person) => <label key={person.enrollmentId}
            className="flex items-center gap-2 rounded border border-stroke bg-paper px-3 py-2 text-[13px]">
            <input type="checkbox" checked={chosen.includes(person.enrollmentId)} onChange={(e) => {
              save.reset(); setChosen((ids) => e.target.checked ? [...ids, person.enrollmentId] : ids.filter((id) => id !== person.enrollmentId));
            }} disabled={!person.enrolled && !chosen.includes(person.enrollmentId)} />
            {person.name}{person.enrolled ? '' : ' (no longer on roster)'}
          </label>)}
        </div>
        {formerSelected.length > 0 ? <div className="flex flex-col gap-2">
          {formerSelected.map((person) => <label key={person.enrollmentId} className="flex items-center gap-2 text-[13px] text-ink-2">
            <input type="checkbox" checked onChange={() => setChosen((ids) => ids.filter((id) => id !== person.enrollmentId))} />
            {person.name} (no longer on roster)
          </label>)}
        </div> : null}
        {chosen.length < 2 ? <p className="text-[13px] text-ink-2">Choose at least two players, or remove this group.</p> : null}
        {invalid ? <p className="text-[13px] text-ink-2">Remove players who are no longer on the roster before saving.</p> : null}
        {save.error ? <ErrorNote>{save.error instanceof ApiError ? save.error.message : 'Could not save this group.'}</ErrorNote> : null}
        <div className="flex gap-3">
          <Button variant="primary" disabled={chosen.length < 2 || invalid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save Group'}</Button>
          <Button variant="secondary" disabled={save.isPending} onClick={() => { setEditing(null); requestAnimationFrame(() => addButton.current?.focus()); }}>Cancel</Button>
        </div>
      </div> : null}
      <dialog ref={confirm} aria-labelledby="remove-play-together" className="m-auto max-w-md rounded-md border border-light bg-paper p-6 text-ink backdrop:bg-black/30"
        onCancel={(e) => { if (remove.isPending) e.preventDefault(); else setRemoving(null); }}>
        <h4 id="remove-play-together" className="font-display text-xl">Remove this Play Together group?</h4>
        <p className="mt-2 text-sm text-ink-2">The players will stay on the roster and existing seating will not change.</p>
        {remove.error ? <ErrorNote>{remove.error instanceof ApiError ? remove.error.message : 'Could not remove this group.'}</ErrorNote> : null}
        <div className="mt-5 flex gap-3">
          <Button autoFocus variant="secondary" disabled={remove.isPending} onClick={() => { confirm.current?.close(); setRemoving(null); }}>Cancel</Button>
          <Button variant="destructive" disabled={remove.isPending} onClick={() => { if (removing) remove.mutate(removing); }}>{remove.isPending ? 'Removing…' : 'Remove Group'}</Button>
        </div>
      </dialog>
    </section>
  );
}
